/** Общие типы домена: используются и сервером, и фронтом. */

export const USER_ROLES = ['admin', 'operator', 'viewer'] as const;
export type UserRole = (typeof USER_ROLES)[number];

export const ROLE_LABEL: Record<UserRole, string> = {
  admin: 'Администратор',
  operator: 'Оператор',
  viewer: 'Наблюдатель',
};

/** Сотрудник BS: staff отвечает клиентам, bot игнорируется, boss — свой, но отдельная роль. */
export const EMPLOYEE_KINDS = ['staff', 'bot', 'boss'] as const;
export type EmployeeKind = (typeof EMPLOYEE_KINDS)[number];

export const TICKET_STATES = ['open', 'promised', 'closed'] as const;
export type TicketState = (typeof TICKET_STATES)[number];

export const RESOLUTION_KINDS = ['positive', 'neutral', 'negative'] as const;
export type ResolutionKind = (typeof RESOLUTION_KINDS)[number];

/** Системные ключи статусов закрытия, на которые опирается движок. */
export const SYSTEM_RESOLUTIONS = {
  answered: 'answered',
  deal: 'deal',
  abandoned: 'abandoned',
  skip: 'skip',
} as const;

export type ResolutionSource = 'auto' | 'user' | 'system';

export const TICKET_EVENT_TYPES = [
  'created',
  'client_message',
  'promised',
  'promise_broken',
  'responded',
  'level_reached',
  'taken',
  'released',
  'moved',
  'commented',
  'resolved',
  'reopened',
  'autoreplied',
  'replied',
] as const;
export type TicketEventType = (typeof TICKET_EVENT_TYPES)[number];

export const GROUP_REQUEST_STATUSES = ['pending', 'running', 'done', 'error', 'cancelled'] as const;
export type GroupRequestStatus = (typeof GROUP_REQUEST_STATUSES)[number];

export interface Employee {
  id: number;
  username: string;
  telegramUserId: string | null;
  displayName: string;
  department: string;
  kind: EmployeeKind;
  columnKeys: string[];
  isGroupAdmin: boolean;
  inviteByDefault: boolean;
  active: boolean;
}

export interface BoardColumn {
  key: string;
  title: string;
  color: string;
  sort: number;
  enabled: boolean;
  isDefault: boolean;
  /** Видна всем пользователям. Личная колонка публичной не бывает. */
  isPublic: boolean;
  /** Владелец личной колонки; null — общая колонка администратора. */
  ownerUserId: number | null;
  /** Участвует в аналитике скорости (по умолчанию да). */
  statsEnabled?: boolean;
}

/** Группа колонок: доступ выдаётся пользователю сразу на все колонки группы. */
export interface ColumnGroup {
  id: number;
  title: string;
  color: string;
  sort: number;
  columnKeys: string[];
}

/** Что выдано пользователю поверх публичных колонок. */
export interface ColumnAccess {
  groupIds: number[];
  columnKeys: string[];
}

export interface ChatCategory {
  key: string;
  title: string;
  color: string;
  sort: number;
  tracked: boolean;
  isDefault: boolean;
  system: boolean;
}

export interface Resolution {
  key: string;
  title: string;
  kind: ResolutionKind;
  color: string;
  sort: number;
  requiresComment: boolean;
  excludeFromSla: boolean;
  system: boolean;
  enabled: boolean;
}

/** Опорные сообщения карточки в переписке (id сообщений Telegram). */
export interface TicketMarks {
  /** Что создало карточку: вопрос клиента; у двойного подтверждения — обе половины пары. */
  request: number[];
  /** Первый ответ (или обещание) сотрудника. */
  firstResponse: number | null;
  /** Сообщение, которым карточку закрыли (ответ, фикс, слово закрытия). */
  close: number | null;
}

export interface TicketDto {
  id: string;
  chatId: string;
  chatTitle: string;
  chatCategory: string | null;
  /** Колонки, за которыми закреплён чат (пусто — раскладка по правилам). Карточки из чата создают только они. */
  chatPinnedColumns: string[];
  columnKey: string;
  state: TicketState;
  levelKey: string | null;
  clientUserId: string | null;
  clientUsername: string | null;
  clientName: string | null;
  firstMessageId: number;
  firstMessageAt: string;
  firstMessageText: string | null;
  lastClientMessageAt: string;
  clientMessagesCount: number;
  hasAttachment: boolean;
  amountUsd: number | null;
  promisedAt: string | null;
  promisedBy: string | null;
  promiseBrokenAt: string | null;
  firstResponseAt: string | null;
  firstResponseBy: string | null;
  /** Автоответчик ответил клиенту в нерабочее время (карточка при этом остаётся открытой). */
  autoreplyAt: string | null;
  /** Карточка создана в нерабочее время (отметка луной, колонка «Пропущенные»). */
  offHours: boolean;
  /** Начало рабочего времени, с которого считается ожидание карточки из нерабочего времени. */
  workStartAt: string | null;
  takenAt: string | null;
  takenBy: string | null;
  closedAt: string | null;
  resolutionKey: string | null;
  resolutionSource: ResolutionSource | null;
  closedBy: string | null;
  comment: string | null;
  deepLink: string | null;
  updatedAt: string;
}

export interface TicketEventDto {
  id: string;
  at: string;
  type: TicketEventType;
  actor: string | null;
  data: Record<string, unknown>;
}

export interface ChatMessageDto {
  messageId: number;
  date: string;
  fromUserId: string | null;
  fromName: string | null;
  fromUsername: string | null;
  text: string | null;
  mediaType: string | null;
  replyToMsgId: number | null;
  isEmployee: boolean;
  /** Отправлено из карточки трекера — кто ответил (сообщение пришло от подключённого аккаунта). */
  sentBy: string | null;
}

/** Упоминание пользователя (@username его сотрудника) в чате. */
export interface MentionDto {
  chatId: string;
  chatTitle: string;
  messageId: number;
  date: string;
  fromName: string | null;
  fromUsername: string | null;
  text: string;
  /** Ссылка на сообщение в Telegram (только супергруппы и каналы). */
  deepLink: string | null;
}

/** Кто сейчас смотрит карточку (см. TicketWatchers на сервере). */
export interface TicketWatcher {
  userId: number;
  name: string;
  username: string | null;
}
/** Вкладка подтверждает просмотр каждые WATCH_HEARTBEAT_SEC; без подтверждения карточка освобождается через WATCH_TTL_SEC. */
export const WATCH_HEARTBEAT_SEC = 20;
export const WATCH_TTL_SEC = 45;

/** Ответ из карточки: ждёт отправки или не ушёл. Отправленный появляется в переписке обычным сообщением. */
export interface OutgoingReplyDto {
  id: string;
  text: string;
  status: 'pending' | 'sending' | 'sent' | 'error' | 'expired';
  error: string | null;
  createdAt: string;
  author: string | null;
}

export interface ChatDto {
  id: string;
  title: string;
  chatType: string;
  memberCount: number | null;
  lastMessageAt: string | null;
  isActive: boolean;
  categoryKey: string | null;
  trackingOverride: boolean | null;
  /** Чат закреплён за колонками: карточки создают только они (пусто — раскладка по правилам). */
  pinnedColumnKeys: string[];
  tracked: boolean;
  note: string | null;
  openTickets: number;
}

export interface SessionUser {
  id: number;
  login: string;
  displayName: string;
  role: UserRole;
}

/** Сообщения WebSocket-канала сервер → браузер. */
export type RealtimeEvent =
  | { type: 'ticket'; ticket: TicketDto }
  /** Карточка ушла в колонку, недоступную пользователю, — убрать с экрана. */
  | { type: 'ticket_hidden'; id: string }
  | { type: 'config'; scope: string }
  | { type: 'group_request'; id: string; status: GroupRequestStatus }
  /** Карточку заняли (watcher) или освободили (null). */
  | { type: 'watch'; ticketId: string; watcher: TicketWatcher | null }
  | { type: 'engine'; status: EngineStatus };

export interface EngineStatus {
  cursor: string | null;
  lastProcessedAt: string | null;
  lastMessageAt: string | null;
  lagSeconds: number | null;
  heartbeatAt: string | null;
}

/** t.me-ссылка на сообщение. SyncDashboard хранит id супергрупп без префикса -100. */
export function telegramDeepLink(chatId: string | number, messageId: number, chatType?: string | null): string | null {
  if (chatType && chatType !== 'supergroup' && chatType !== 'channel') return null;
  const id = String(chatId).replace(/^-100/, '').replace(/^-/, '');
  return `https://t.me/c/${id}/${messageId}`;
}
